/**
 * Single source of truth for estimating an edge/relation label's on-canvas
 * footprint (width, height, minimum edge length it needs).
 *
 * tldraw measures text against real fonts at render time, which this module
 * deliberately does not call: layout algorithms run well before any shape
 * exists on the page, and synchronous DOM text measurement per edge would be
 * expensive on a dense board. Instead this is a monospace-ish word-wrap
 * approximation — good enough to reserve room during layout;
 * `lengthenEdgesForLabels` (compactChartLayout.js) is the safety net that
 * grows a hosting segment when the estimate still runs short.
 *
 * Consumers: chartArrowRouting.js (min edge length, label placement),
 * elkLayeredLayout.js (edge label boxes), radialLayout.js (ring radius),
 * forceAtlas2Layout.js (global scale target).
 */

/** Average glyph width in px for the draw-style label font at default zoom. */
export const LABEL_CHAR_WIDTH = 9;

/** Labels wrap onto at most this many lines before we stop growing height. */
export const MAX_LABEL_LINES = 3;

/**
 * Preferred wrap width in px. tldraw wraps relation labels aggressively
 * (e.g. "post-El Mencho leader of" becomes three lines). A single long
 * word may still force a wider line.
 */
export const MAX_LABEL_WRAP_WIDTH = 90;

/** Line height in px for a wrapped label line (font size + leading). */
export const LABEL_LINE_HEIGHT = 16;

/** Vertical padding inside the label's background pill, top + bottom. */
export const LABEL_VERTICAL_PADDING = 10;

/** Clearance reserved on each side of a label along its edge. */
export const LABEL_EDGE_PADDING = 48;

/** Floor for edge length even when a label is short or absent. */
export const MIN_LABELED_EDGE_LENGTH = 120;

/**
 * @param {string|null|undefined} label
 * @returns {string} Trimmed text, empty string for nullish/blank input.
 */
function normalizeLabelText(label) {
    return String(label || '').trim();
}

/**
 * Greedy word wrap at `maxWidth`, then grow that width until the text fits
 * in {@link MAX_LABEL_LINES} (so leftover words are not silently clipped
 * into an under-wide box).
 *
 * @param {string} text
 * @returns {Array<{text: string, w: number}>}
 */
export function wrapLabelLines(text) {
    const words = normalizeLabelText(text).split(/\s+/).filter(Boolean);
    if (words.length === 0) return [];

    const space = LABEL_CHAR_WIDTH;
    const wordWidths = words.map((word) => word.length * LABEL_CHAR_WIDTH);
    const longestWord = Math.max(0, ...wordWidths);

    const pack = (maxWidth) => {
        const lines = [];
        let parts = [];
        let width = 0;
        for (let i = 0; i < words.length; i += 1) {
            const extra = parts.length ? space + wordWidths[i] : wordWidths[i];
            if (parts.length && width + extra > maxWidth + 1e-6) {
                lines.push({text: parts.join(' '), w: width});
                parts = [words[i]];
                width = wordWidths[i];
            } else {
                parts.push(words[i]);
                width += extra;
            }
        }
        if (parts.length) lines.push({text: parts.join(' '), w: width});
        return lines;
    };

    let maxWidth = Math.max(MAX_LABEL_WRAP_WIDTH, longestWord);
    let lines = pack(maxWidth);
    const hardMax = Math.max(maxWidth, words.join(' ').length * LABEL_CHAR_WIDTH);
    while (lines.length > MAX_LABEL_LINES && maxWidth < hardMax) {
        maxWidth += LABEL_CHAR_WIDTH * 2;
        lines = pack(maxWidth);
    }
    if (lines.length > MAX_LABEL_LINES) {
        const head = lines.slice(0, MAX_LABEL_LINES - 1);
        const tail = lines.slice(MAX_LABEL_LINES - 1);
        const merged = tail.map((line) => line.text).join(' ');
        head.push({text: merged, w: merged.length * LABEL_CHAR_WIDTH});
        return head;
    }
    return lines;
}

/**
 * Estimated number of wrapped lines a label renders as, from 0 (no label) up
 * to {@link MAX_LABEL_LINES}.
 *
 * @param {string|null|undefined} label
 * @returns {number}
 */
export function estimateLabelLineCount(label) {
    const text = normalizeLabelText(label);
    if (!text) return 0;
    return Math.min(MAX_LABEL_LINES, Math.max(1, wrapLabelLines(text).length));
}

/**
 * Estimated label box width in px — the widest wrapped line.
 *
 * @param {string|null|undefined} label
 * @returns {number}
 */
export function estimateLabelWidth(label) {
    const text = normalizeLabelText(label);
    if (!text) return 0;
    const lines = wrapLabelLines(text);
    return lines.reduce((max, line) => Math.max(max, line.w), 0);
}

/**
 * Estimated label box height in px, accounting for line wrapping.
 *
 * @param {string|null|undefined} label
 * @returns {number}
 */
export function estimateLabelHeight(label) {
    const lines = estimateLabelLineCount(label);
    if (lines === 0) return 0;
    return lines * LABEL_LINE_HEIGHT + LABEL_VERTICAL_PADDING;
}

/**
 * Estimate how long the visible edge segment should be so a relation label
 * fits on at most {@link MAX_LABEL_LINES} lines with breathing room on both
 * sides.
 *
 * @param {string|null|undefined} label
 * @returns {number}
 */
export function estimateMinEdgeLengthForLabel(label) {
    const text = normalizeLabelText(label);
    if (!text) return MIN_LABELED_EDGE_LENGTH;
    return Math.max(MIN_LABELED_EDGE_LENGTH, estimateLabelWidth(text) + LABEL_EDGE_PADDING * 2);
}

/**
 * Full label box estimate `{w, h}`, for callers (ELK, box-collision scoring)
 * that need both dimensions together.
 *
 * @param {string|null|undefined} label
 * @returns {{w: number, h: number}}
 */
export function estimateLabelBox(label) {
    return {w: estimateLabelWidth(label), h: estimateLabelHeight(label)};
}
